import {getMyStuffPageHash} from '@Libraries/my-stuff/my-stuff-library';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import type {ContactsContainerQuery} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {
  ContactsTabSelection,
  HAS_SEEN_CONTACTS_SIDEBAR_INTRO_POPUP,
  SIDEBAR_ACTIVITY_INDICATOR_TIME,
  SIGNUP_FORM_ITEM_INDICATOR_TIME,
  SmartMailingListTypes,
} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import type {UserMailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {AudienceCustomFieldData, RawAudience, UserCustomFieldMeta} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {type RawAudienceFieldNames} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import {getSeparatedStringFromCamelCase, LetterCase} from '@Utils/string.util';
import {toCsvRow, toCsvText} from '@Utils/csv.util';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {getUserId, type HasUserSeenData, isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import type {AjaxResponse} from '@Utils/ajax.util';

export const isEmailCampaignMailingListsPage = (): boolean => {
  return getMyStuffPageHash() === MY_STUFF_HASHES.emailCampaignMailingLists;
};

export const isEmailCampaignSignupFormsPage = (): boolean => {
  return getMyStuffPageHash() === MY_STUFF_HASHES.emailCampaignSignupForms;
};

export const isEmailCampaignContactsPage = (): boolean => {
  return isEmailCampaignMailingListsPage() || isEmailCampaignSignupFormsPage();
};

export const isPageRelatedToMailingLists = (): boolean => {
  const pageHash = getMyStuffPageHash();
  return pageHash === MY_STUFF_HASHES.mailingLists || pageHash === MY_STUFF_HASHES.emailCampaignMailingLists;
};

export const isPageRelatedToSignUpForms = (): boolean => {
  const pageHash = getMyStuffPageHash();
  return pageHash === MY_STUFF_HASHES.signupForms || pageHash === MY_STUFF_HASHES.emailCampaignSignupForms;
};

export const showMailingListDeletionForbiddenDialog = (): void => {
  openModal(MODAL_IDS.ERROR_MODAL_ID, {
    message: window.i18next.t('pmwjs_mailing_list_deletion_forbidden'),
    log: false,
  });
};

export const isMailingListDeleteForbidden = (response: AjaxResponse): boolean => {
  return response.code === 'mailing-list-in-signup-form';
};

export const isPageRelatedToContacts = (): boolean => {
  return isPageRelatedToMailingLists() || isPageRelatedToSignUpForms();
};

export const isContactsExtraSmallScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isExtraSmallScreen;
};

export const isContactsSmallScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isSmallScreen;
};

export const isContactsMediumScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isMediumScreen;
};

export const isContactsLargeScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isLargeScreen;
};

export const isContactsExtraLargeScreen = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return !!params.isExtraLargeScreen;
};

export const isContactsTabSize = (params: ContactsContainerQuery | null): boolean => {
  if (!params) {
    return false;
  }

  return isContactsMediumScreen(params) || isContactsSmallScreen(params);
};

export const hidePaddedTopBarMyStuff = (): void => {
  document.querySelector('.js-padded-top-bar')?.classList.add('_hidden');
};

export const unhidePaddedTopBarMyStuff = (): void => {
  document.querySelector('.js-padded-top-bar')?.classList.remove('_hidden');
};

export const getTestMailingList = (): UserMailingList => {
  return {
    idOwner: 0,
    isSharedInTeam: false,
    id: 1,
    name: 'Test Mailing List',
    createdOn: '',
    isSmart: false,
    isSpam: false,
  };
};

export const isMailingListsContactsTabSelected = (tabSelection: ContactsTabSelection): boolean => {
  return tabSelection === ContactsTabSelection.MAILING_LISTS;
};

export const isSignupFormsTabSelected = (tabSelection: ContactsTabSelection): boolean => {
  return tabSelection === ContactsTabSelection.SIGNUP_FORMS;
};

export const stringifyCookieValueForMailingListsSelection = (mailingListsSelected: UserMailingList): string => {
  return `${mailingListsSelected.id}`;
};

export const isMailingListSharedByTeamMember = (mailingList: UserMailingList): boolean => {
  return mailingList.isSharedInTeam && mailingList.idOwner !== getUserId();
};

export const decodeCookieValueForMailingListsSelection = (value: string): number[] => {
  if (!value || typeof value !== 'string') return [];

  return value
    .split(',')
    .map((id) => Number(id.trim()))
    .filter((id) => !isNaN(id));
};

const sortUserCustomFields = (userCustomFieldsMeta: UserCustomFieldMeta[]): UserCustomFieldMeta[] => {
  return [...userCustomFieldsMeta].sort((a, b) => a.idUserHasCustomField - b.idUserHasCustomField);
};

export const stringifyRawAudience = (rawAudience: RawAudience, userCustomFieldsMeta: UserCustomFieldMeta[]): string => {
  const cellValues: string[] = [
    rawAudience.email,
    rawAudience.firstName ?? '',
    rawAudience.lastName ?? '',
    rawAudience.phoneNumber ?? '',
    rawAudience.address ?? '',
    processBirthdayFromServer(rawAudience.birthday),
  ];

  const customFields = rawAudience.customFields ?? {};
  for (const customFieldMeta of userCustomFieldsMeta) {
    cellValues.push(customFields[customFieldMeta.idUserHasCustomField] ?? '');
  }

  return toCsvRow(cellValues);
};

export const getCsvHeaders = (userCustomFieldsMeta: UserCustomFieldMeta[]): string[] => {
  const defaultFields: RawAudienceFieldNames[] = ['email', 'firstName', 'lastName', 'phoneNumber', 'address', 'birthday'];
  const sortedCustomFields = sortUserCustomFields(userCustomFieldsMeta);

  const customFieldNames = sortedCustomFields.map((customFieldMeta: UserCustomFieldMeta): string => {
    return customFieldMeta.label;
  });

  return [...(defaultFields as string[]), ...customFieldNames].map((fieldName) => {
    return getSeparatedStringFromCamelCase(fieldName, LetterCase.SENTENCE_CASE);
  });
};

export const getMailingListsDataForCsv = (mailingLists: UserMailingList, userCustomFieldsMeta: UserCustomFieldMeta[]): string[] => {
  const data: string[] = [];
  const sortedUserCustomFieldsMeta = sortUserCustomFields(userCustomFieldsMeta);

  const mailingListContacts = mailingLists.mailingListContacts;

  if (mailingListContacts !== undefined) {
    for (let j = 0; j < mailingListContacts.length; j++) {
      const string = stringifyRawAudience(mailingListContacts[j], sortedUserCustomFieldsMeta);
      data.push(string);
    }
  }

  return data;
};

export const getIndexForCustomFieldWithId = (idCustomField: number, userCustomFields: AudienceCustomFieldData): number => {
  const index = -1;
  const indices = Object.keys(userCustomFields);

  for (let i = 0; i < indices.length; i++) {
    const index = parseFloat(indices[i]);
    const data = userCustomFields[index];
    // @ts-ignore
    const idUserHasCustomField = data.idUserHasCustomField;

    if (idCustomField === idUserHasCustomField) {
      return i;
    }
  }

  return index;
};

export const getMailingListCsvText = (mailingListToDownload: UserMailingList, userCustomFieldMeta: UserCustomFieldMeta[]): string => {
  return toCsvText(getCsvHeaders(userCustomFieldMeta), getMailingListsDataForCsv(mailingListToDownload, userCustomFieldMeta));
};

export const onClickDownloadCsv = (mailingListToDownload: UserMailingList, userCustomFieldMeta: UserCustomFieldMeta[]): void => {
  if (!mailingListToDownload) {
    return;
  }

  const fileName = mailingListToDownload.name;
  const csvDataToExport = getMailingListCsvText(mailingListToDownload, userCustomFieldMeta);

  const csvBlob = new Blob([csvDataToExport], {
    type: `data:text/csv;charset=utf-8`,
  });

  const encodedUri = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');

  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
};

export const hasUserSeenContactsSidebarIntroPopup = async (): Promise<boolean> => {
  const value = readCookie(HAS_SEEN_CONTACTS_SIDEBAR_INTRO_POPUP);
  if (value !== null) {
    return !!value;
  }

  if (!isUserLoggedIn()) {
    return false;
  }

  const data = (await window.PMW.writeLocal('emailmarketing/hasUserSeenContactsSidebarIntroPopup', {})) as HasUserSeenData;

  if ('hasUserSeen' in data) {
    return data.hasUserSeen;
  }

  return false;
};

export const markUserHasSeenContactsIntroPopup = (): void => {
  void window.PMW.writeLocal('emailmarketing/markUserSeenContactsSidebarIntroPopup');
};

export const getCurrentUTCDate = (): string => {
  const now = new Date();
  return new Date(now.getTime() + now.getTimezoneOffset() * 60000).toISOString();
};

export const getDateFromUTCValue = (stringifiedUTCDate: string): Date => {
  return new Date(stringifiedUTCDate);
};

export const setSidebarActivityIndicatorCookie = (): void => {
  const value = getCurrentUTCDate();
  setCookie(SIDEBAR_ACTIVITY_INDICATOR_TIME, value, 1);
};

export const getSidebarActivityIndicatorCookieValue = (): Date | null => {
  const value = readCookie(SIDEBAR_ACTIVITY_INDICATOR_TIME);

  if (!value) {
    return null;
  }

  return getDateFromUTCValue(value);
};

export const setSignupFormActivityIndicatorCookie = (): void => {
  const value = getCurrentUTCDate();
  setCookie(SIGNUP_FORM_ITEM_INDICATOR_TIME, value, 1);
};

export const getSignupFormActivityIndicatorCookie = (): Date | null => {
  const value = readCookie(SIGNUP_FORM_ITEM_INDICATOR_TIME);

  if (!value) {
    return null;
  }

  return getDateFromUTCValue(value);
};

export const fireSmartMailingListSelectionEvent = (smartMailingListType: SmartMailingListTypes): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_AUTO_MAILING_LIST, {
    [GA4EventParamName.AUTO_MAILING_LIST_TYPE]: smartMailingListType,
  });
};

export const handleSmartMailingListsGaEvents = (selectedMailingListIds: number[]): void => {
  const smartMailingListTypeInformation = window.PMW.redux.store.getState().mailingListContacts.smartMailingListsTooltipMap;

  if (!smartMailingListTypeInformation) {
    return;
  }

  for (let i = 0; i < selectedMailingListIds.length; i++) {
    const mailingListId = selectedMailingListIds[i];

    const isAllContactsMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.ALL_CONTACTS];
    const isLeastEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.LEAST_ENGAGED];
    const isMostEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.MOST_ENGAGED];
    const isSomewhatEngagedMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.SOMEWHAT_ENGAGED];
    const isEventAttendeesMailingList = mailingListId === smartMailingListTypeInformation[SmartMailingListTypes.EVENT_ATTENDEES];

    if (isAllContactsMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.ALL_CONTACTS);
    }

    if (isLeastEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.LEAST_ENGAGED);
    }

    if (isMostEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.MOST_ENGAGED);
    }

    if (isSomewhatEngagedMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.SOMEWHAT_ENGAGED);
    }

    if (isEventAttendeesMailingList) {
      fireSmartMailingListSelectionEvent(SmartMailingListTypes.EVENT_ATTENDEES);
    }
  }
};

export const getInternationalisedLabel = (fieldName: RawAudienceFieldNames): string => {
  if (fieldName === 'email') {
    return window.i18next.t('pmwjs_email');
  }

  if (fieldName === 'firstName') {
    return window.i18next.t('pmwjs_first_name');
  }

  if (fieldName === 'lastName') {
    return window.i18next.t('pmwjs_last_name');
  }

  if (fieldName === 'phoneNumber') {
    return window.i18next.t('pmwjs_phone_number');
  }

  if (fieldName === 'address') {
    return window.i18next.t('pmwjs_address');
  }

  if (fieldName === 'birthday') {
    return window.i18next.t('pmwjs_birthday');
  }

  return '';
};

export const processBirthdayFromServer = (birthday: RawAudience['birthday']): string => {
  if (!birthday) {
    return '';
  }

  if (typeof birthday === 'string') {
    return birthday;
  }

  if (typeof birthday === 'object' && 'date' in birthday) {
    if (!('date' in birthday)) {
      return '';
    }

    const date = new Date(birthday.date as string);

    if (isNaN(date.getTime())) {
      return '';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  return '';
}

export const fetchMailingListById = async (mailingListId: number): Promise<UserMailingList | null> => {
  const response = (await window.PMW.readLocal('emailmarketing/getMailingListById', {idMailingList: mailingListId})) as {mailingList?: UserMailingList};
  return response.mailingList ?? null;
};

export const getMailingListContactsViewHash = (mailingList: UserMailingList | null): string => {
  const mailingListUriSegment = mailingList && !mailingList.isSmart ? `/${mailingList.id}` : '';
  return `${MY_STUFF_HASHES.mailingLists}${mailingListUriSegment}`;
};

/**
 * Entering or leaving a non-smart mailing list's contacts view is a real hash navigation
 * (URI-first): the hashchange re-renders the my-stuff shell top-down with the new URL, and
 * MailingListSpecificView reconciles the redux selection to follow it. Smart mailing lists
 * are not deep-linkable; their selection is plain redux state.
 */
export const navigateToMailingListContactsView = (mailingList: UserMailingList | null): void => {
  window.location.hash = getMailingListContactsViewHash(mailingList);
};